import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API, timeout: 20000 });

// Attach JWT: admin token on admin pages, customer token on storefront pages.
client.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const path = window.location.pathname || '';
  const onAdmin = path.startsWith('/admin');
  const adminToken = localStorage.getItem('wss_token');
  const customerToken = localStorage.getItem('wss_customer_token');
  const token = onAdmin ? adminToken : (customerToken || adminToken);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const api = {
  // Products
  listProducts: (params = {}) => client.get('/products', { params }).then((r) => r.data),
  getProduct: (slug) => client.get(`/products/${slug}`).then((r) => r.data),
  createProduct: (data) => client.post('/products', data).then((r) => r.data),
  updateProduct: (id, data) => client.patch(`/products/${id}`, data).then((r) => r.data),
  deleteProduct: (id) => client.delete(`/products/${id}`).then((r) => r.data),

  // Customers
  listCustomers: (params = {}) => client.get('/customers', { params }).then((r) => r.data),
  newsletter: (email) => client.post('/customers/newsletter', { email }).then((r) => r.data),

  // Orders
  listOrders: (params = {}) => client.get('/orders', { params }).then((r) => r.data),
  getOrder: (orderNumber) => client.get(`/orders/${orderNumber}`).then((r) => r.data),
  createOrder: (data) => client.post('/orders', data).then((r) => r.data),
  submitVoucherCode: (orderNumber, data) => client.post(`/orders/${orderNumber}/voucher-code`, data).then((r) => r.data),

  // Posts
  listPosts: () => client.get('/posts').then((r) => r.data),
  createPost: (data) => client.post('/posts', data).then((r) => r.data),
  updatePost: (id, data) => client.patch(`/posts/${id}`, data).then((r) => r.data),
  deletePost: (id) => client.delete(`/posts/${id}`).then((r) => r.data),
  publishPost: (id) => client.post(`/posts/${id}/publish`).then((r) => r.data),

  // Categories
  listCategories: (params = {}) => client.get('/categories', { params }).then((r) => r.data),
  createCategory: (data) => client.post('/categories', data).then((r) => r.data),
  updateCategory: (id, data) => client.patch(`/categories/${id}`, data).then((r) => r.data),
  deleteCategory: (id) => client.delete(`/categories/${id}`).then((r) => r.data),

  // Pages
  listPages: (params = {}) => client.get('/pages', { params }).then((r) => r.data),
  getPage: (slug) => client.get(`/pages/${slug}`).then((r) => r.data),
  createPage: (data) => client.post('/pages', data).then((r) => r.data),
  updatePage: (id, data) => client.patch(`/pages/${id}`, data).then((r) => r.data),
  deletePage: (id) => client.delete(`/pages/${id}`).then((r) => r.data),

  // Coupons
  listCoupons: () => client.get('/coupons').then((r) => r.data),
  createCoupon: (data) => client.post('/coupons', data).then((r) => r.data),
  updateCoupon: (id, data) => client.patch(`/coupons/${id}`, data).then((r) => r.data),
  deleteCoupon: (id) => client.delete(`/coupons/${id}`).then((r) => r.data),
  validateCoupon: (code, subtotal) => client.post(`/coupons/validate`, null, { params: { code, subtotal } }).then((r) => r.data),

  // Reviews
  listReviews: (params = {}) => client.get('/reviews', { params }).then((r) => r.data),
  reviewSummary: (productId) => client.get(`/reviews/summary/${productId}`).then((r) => r.data),
  createReview: (data) => client.post('/reviews', data).then((r) => r.data),
  approveReview: (id, approved = true) => client.patch(`/reviews/${id}/approve`, null, { params: { approved } }).then((r) => r.data),
  deleteReview: (id) => client.delete(`/reviews/${id}`).then((r) => r.data),

  // Site settings (branding, contact, menus)
  getSite: () => client.get('/settings/site').then((r) => r.data),
  putSite: (data) => client.put('/settings/site', data).then((r) => r.data),

  // Settings
  getSEO: () => client.get('/settings/seo').then((r) => r.data),
  putSEO: (data) => client.put('/settings/seo', data).then((r) => r.data),
  getPayments: () => client.get('/settings/payments').then((r) => r.data),
  putPayments: (data) => client.put('/settings/payments', data).then((r) => r.data),
  getSecurity: () => client.get('/settings/security').then((r) => r.data),
  putSecurity: (data) => client.put('/settings/security', data).then((r) => r.data),
  getStats: () => client.get('/settings/stats').then((r) => r.data),

  // Shipping
  shippingZones: () => client.get('/shipping/zones').then((r) => r.data),
  shippingQuote: (country, subtotal) => client.get('/shipping/quote', { params: { country, subtotal } }).then((r) => r.data),
  voucherProviders: () => client.get('/shipping/voucher-providers').then((r) => r.data),
  getShippingSettings: () => client.get('/shipping/settings').then((r) => r.data),
  putShippingSettings: (data) => client.put('/shipping/settings', data).then((r) => r.data),

  // Geo
  myGeo: () => client.get('/geo/me').then((r) => r.data),
  // Public security flags (Turnstile site key)
  securityPublic: () => client.get('/settings/security/public').then((r) => r.data),

  // Media library (admin uploads, public file serve)
  mediaList: () => client.get('/media').then((r) => r.data),
  mediaUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  mediaDelete: (id) => client.delete(`/media/${id}`).then((r) => r.data),
};

export const CATEGORIES = [
  { id: 'randm', name: 'Randm', image: 'https://images.unsplash.com/photo-1601582037054-66c0611f6033?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { id: '15k', name: '15K Tornado', image: 'https://images.unsplash.com/photo-1666402667623-664de2c19195?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  { id: '20k', name: '20K Tornado', image: 'https://images.unsplash.com/photo-1601568656096-bde429d5d386?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
];

export default api;
